import { MiscUtils } from './MiscUtils';

/**
 * Hold multiple keys and values and output as an assembled URL query string
 */
class ImmutableQuery {
  /**
   * Return a Object populated with keys and values from given query string
   * @param queryString A string containing one or more keys and values
   * formatted like 'key1=value1&key2=value2'
   */
  static convertToObject(queryString: string) {
    return queryString.split('&').reduce((query: object, part: string) => {
      const [key, value] = part.split('=', 2);
      if (key in query) {
        let valueArray = query[key];
        valueArray = Array.isArray(valueArray) ? valueArray : [valueArray];
        valueArray.push(value);

        query[key] = valueArray;
      } else if (key) {
        query[key] = value;
      }
      return query;
    }, {});
  }

  /**
   * Returns a boolean indicating if two give ImmutableQuerys or objects have
   * equal values
   */
  static equals(queryA: ImmutableQuery | {}, queryB: ImmutableQuery | {}) {
    return (
      ImmutableQuery.toQueryString(queryA) ===
      ImmutableQuery.toQueryString(queryB)
    );
  }

  /**
   * Return a new ImmutableQuery created from the Object given
   *
   * @param obj Properties to assign to the new ImmutableQuery
   */
  static fromObject(obj: {}) {
    return new ImmutableQuery().set(obj);
  }

  /**
   * Return a HTTP query string constructed from all properties and values in
   * ImmutableQuery instance
   */
  static toQueryString(query: ImmutableQuery | {}) {
    const keys = Object.keys(query);
    return keys.length
      ? '?' +
          keys
            .sort(MiscUtils.alphabetize)
            .map((key) => {
              if (Array.isArray(query[key])) {
                return `${key}=${query[key]
                  .sort(MiscUtils.alphabetize)
                  .join(`&${key}=`)}`;
              }
              return `${key}=${query[key]}`;
            })
            .join('&')
      : '';
  }

  /**
   * Return a new ImmutableQuery instance that is either empty or optionally
   * includes only values from a whitelist
   *
   * @param whitelist The key names of the properties which will carry over into
   * the new ImmutableQuery instance
   */
  clear(whitelist: string[] = []) {
    const whitelistedQuery = {};
    whitelist.forEach(
      (key) => this.hasOwnProperty(key) && (whitelistedQuery[key] = this[key]),
    );
    return new ImmutableQuery().set(whitelistedQuery);
  }

  /**
   * Return a new ImmutableQuery instance with the same properties and values as
   * the original
   */
  clone() {
    return Object.assign(new ImmutableQuery(), this);
  }

  /**
   * Returns a boolean indicating if give ImmutableQuery has an equal value to
   * the current instance
   */
  isEqual(other: ImmutableQuery | {}) {
    return this.toQueryString() === ImmutableQuery.toQueryString(other);
  }

  /**
   * Returns the given key's value or the given default
   */
  get(key: string) {
    return this.hasOwnProperty(key) ? this[key] : '';
  }

  /**
   * Return a new ImmutableQuery instance cloned from the original with the
   * given properties assigned on top
   *
   * @param object New properties to assign to cloned instance
   */
  public set(object: {}): ImmutableQuery;
  public set(queryString: string): ImmutableQuery;
  public set(objOrQueryString: {} | string): ImmutableQuery {
    const inObject =
      typeof objOrQueryString === 'string'
        ? ImmutableQuery.convertToObject(objOrQueryString)
        : objOrQueryString;

    const aggregateObj = Object.assign(this.clone(), inObject);

    Object.keys(aggregateObj).forEach((key) => {
      const value = aggregateObj[key];
      if (value === null) {
        delete aggregateObj[key];
      }
    });
    return aggregateObj;
  }

  /**
   * Return a HTTP query string constructed from all properties and values in
   * ImmutableQuery instance
   */
  toQueryString() {
    return ImmutableQuery.toQueryString(this);
  }
}

export { ImmutableQuery };
